/* Copyright (c) 2023 TORITI */

import Request from './request';

export default class AdminAccountService {
  /**
   * Get list of admin accounts
   */
  static async getList(query = {}) {
    return new Promise((resolve) => {
      Request.send({
        method: 'GET',
        path: 'cms-admin/accounts',
        query
      }).then((result = {}) => {
        console.log('Admin accounts list response:', result);

        if (result.success && result.data) {
          return resolve({
            success: true,
            data: result.data,
            pagination: result.pagination,
            message: result.message
          });
        }

        return resolve(result);
      }).catch((error) => {
        console.error('Admin accounts list error:', error);
        return resolve({
          success: false,
          errors: [{ message: error.message || 'Failed to fetch admin accounts' }]
        });
      });
    });
  }

  /**
   * Get admin account by ID
   */
  static async getById(id) {
    return new Promise((resolve) => {
      Request.send({
        method: 'GET',
        path: `cms-admin/accounts/${id}`
      }).then((result = {}) => {
        console.log('Admin account detail response:', result);

        if (result.success && result.data) {
          return resolve({
            success: true,
            data: result.data,
            message: result.message
          });
        }

        return resolve(result);
      }).catch((error) => {
        console.error('Admin account detail error:', error);
        return resolve({
          success: false,
          errors: [{ message: error.message || 'Failed to fetch admin account' }]
        });
      });
    });
  }

  /**
   * Create new admin account
   */
  static async create(data = {}) {
    return new Promise((resolve) => {
      const apiData = {
        email: data.email,
        phoneE164: data.phoneE164,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        password: data.password
      };

      console.log('Creating admin account with data:', apiData);

      Request.send({
        method: 'POST',
        path: 'cms-admin/accounts',
        data: apiData
      }).then((result = {}) => {
        console.log('Create admin account response:', result);

        if (result.success) {
          return resolve({
            success: true,
            data: result.data,
            message: result.message || 'Admin account created successfully'
          });
        }

        return resolve(result);
      }).catch((error) => {
        console.error('Create admin account error:', error);
        return resolve({
          success: false,
          message: error.message || 'Failed to create admin account',
          errors: [{ message: error.message || 'Failed to create admin account' }]
        });
      });
    });
  }

  /**
   * Update admin account
   */
  static async update(id, data = {}) {
    return new Promise((resolve) => {
      const apiData = {
        firstName: data.firstName,
        lastName: data.lastName,
        phoneE164: data.phoneE164,
        role: data.role,
        status: data.status
      };

      // Only include password if provided
      if (data.password) {
        apiData.password = data.password;
      }

      console.log('Updating admin account with data:', apiData);

      Request.send({
        method: 'PUT',
        path: `cms-admin/accounts/${id}`,
        data: apiData
      }).then((result = {}) => {
        console.log('Update admin account response:', result);

        if (result.success) {
          return resolve({
            success: true,
            data: result.data,
            message: result.message || 'Admin account updated successfully'
          });
        }

        return resolve(result);
      }).catch((error) => {
        console.error('Update admin account error:', error);
        return resolve({
          success: false,
          message: error.message || 'Failed to update admin account',
          errors: [{ message: error.message || 'Failed to update admin account' }]
        });
      });
    });
  }

  /**
   * Delete admin account
   */
  static async delete(id) {
    return new Promise((resolve) => {
      console.log('Deleting admin account:', id);

      Request.send({
        method: 'DELETE',
        path: `cms-admin/accounts/${id}`
      }).then((result = {}) => {
        console.log('Delete admin account response:', result);

        return resolve({
          success: true,
          message: result.message || 'Admin account deleted successfully'
        });
      }).catch((error) => {
        console.error('Delete admin account error:', error);
        return resolve({
          success: false,
          message: error.message || 'Failed to delete admin account',
          errors: [{ message: error.message || 'Failed to delete admin account' }]
        });
      });
    });
  }

  /**
   * Assign merchant access to admin
   */
  static async assignMerchantAccess(adminId, data = {}) {
    return new Promise((resolve) => {
      const apiData = {
        merchantId: data.merchantId,
        accessLevel: data.accessLevel
      };

      console.log('Assigning merchant access:', apiData);

      Request.send({
        method: 'POST',
        path: `cms-admin/accounts/${adminId}/merchant-access`,
        data: apiData
      }).then((result = {}) => {
        console.log('Assign merchant access response:', result);

        if (result.success) {
          return resolve({
            success: true,
            data: result.data,
            message: result.message || 'Merchant access assigned successfully'
          });
        }

        return resolve(result);
      }).catch((error) => {
        console.error('Assign merchant access error:', error);
        return resolve({
          success: false,
          message: error.message || 'Failed to assign merchant access',
          errors: [{ message: error.message || 'Failed to assign merchant access' }]
        });
      });
    });
  }

  /**
   * Revoke merchant access from admin
   */
  static async revokeMerchantAccess(adminId, merchantId) {
    return new Promise((resolve) => {
      console.log('Revoking merchant access:', { adminId, merchantId });

      Request.send({
        method: 'DELETE',
        path: `cms-admin/accounts/${adminId}/merchant-access/${merchantId}`
      }).then((result = {}) => {
        console.log('Revoke merchant access response:', result);

        return resolve({
          success: true,
          message: result.message || 'Merchant access revoked successfully'
        });
      }).catch((error) => {
        console.error('Revoke merchant access error:', error);
        return resolve({
          success: false,
          message: error.message || 'Failed to revoke merchant access',
          errors: [{ message: error.message || 'Failed to revoke merchant access' }]
        });
      });
    });
  }

  /**
   * Check if admin can impersonate merchant
   */
  static async checkImpersonatePermission(adminId, merchantId) {
    return new Promise((resolve) => {
      Request.send({
        method: 'GET',
        path: `cms-admin/accounts/${adminId}/can-impersonate/${merchantId}`
      }).then((result = {}) => {
        console.log('Check impersonate permission response:', result);

        if (result.success && result.data) {
          return resolve({
            success: true,
            canImpersonate: result.data.canImpersonate,
            message: result.message
          });
        }

        return resolve(result);
      }).catch((error) => {
        console.error('Check impersonate permission error:', error);
        return resolve({
          success: false,
          canImpersonate: false,
          message: error.message || 'Failed to check permission'
        });
      });
    });
  }
}
